<template>
    <div class="wrap">
        <div class="content">
            <div class="flex items-center justify-between mb-4">
                <h2 class="text-xl font-semibold text-black dark:text-white">
                    {{ $t('calendar.week', { week: getCurrentWeekNumber() }) }} ({{ formatDateRange() }})
                </h2>
                <div class="flex gap-4">
                    <a
                        :href="`/tools/time/calendar?startDate=${getPreviousWeek()}`"
                        class="rounded-sm bg-amber-400 hover:bg-amber-600 px-2 py-1 font-bold text-black"
                    >
                        {{ $t('calendar.previous_week') }}
                    </a>
                    <a
                        :href="`/tools/time/calendar?startDate=${getCurrentWeekStart()}`"
                        class="rounded-sm bg-green-500 hover:bg-green-600 px-2 py-1 font-bold text-white"
                    >
                        {{ $t('calendar.current_week') }}
                    </a>
                    <a
                        :href="`/tools/time/calendar?startDate=${getNextWeek()}`"
                        class="rounded-sm bg-amber-400 hover:bg-amber-600 px-2 py-1 font-bold text-black"
                    >
                        {{ $t('calendar.next_week') }}
                    </a>
                </div>
            </div>
            <div class="mt-4">
                <DayPilotCalendar :config="calendarConfig" ref="calendarRef" />
            </div>
        </div>

        <!-- Event Edit Modal -->
        <div
            v-if="showEventModal"
            class="fixed inset-0 bg-gray-500/75 flex items-center justify-center"
            @click="closeEventModal"
        >
            <div
                class="bg-white dark:bg-gray-800 p-6 rounded-lg w-96 shadow-xl"
                @click.stop
            >
                <h3 class="text-lg font-semibold mb-4 text-black dark:text-gray-400">
                    {{ selectedEvent ? $t('calendar.edit_event') : $t('calendar.new_event') }}
                </h3>

                <div class="space-y-4">
                    <div>
                        <label class="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-900">{{ $t('calendar.jira_id') }}</label>
                        <input
                            type="text"
                            v-model="eventForm.ticket"
                            @change="handleTicketChange"
                            class="w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 focus:border-blue-500 dark:focus:border-blue-500 focus:ring-blue-500 dark:focus:ring-blue-500"
                            :placeholder="$t('calendar.jira_id_placeholder')"
                        />
                    </div>

                    <div>
                        <label class="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-900">{{ $t('calendar.customer') }}</label>
                        <select
                            v-model="eventForm.customer"
                            class="w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 focus:border-blue-500 dark:focus:border-blue-500 focus:ring-blue-500 dark:focus:ring-blue-500"
                            @change="handleCustomerChange"
                        >
                            <option value="" class="dark:bg-gray-700">{{ $t('calendar.select_customer') }}</option>
                            <option
                                v-for="customer in activeCustomers"
                                :key="customer.customer.id"
                                :value="customer.customer.id"
                                class="dark:bg-gray-700"
                            >
                                {{ customer.customer.name }}
                            </option>
                        </select>
                    </div>

                    <div>
                        <label class="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-900">{{ $t('calendar.project') }}</label>
                        <select
                            v-model="eventForm.project"
                            class="w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 focus:border-blue-500 dark:focus:border-blue-500 focus:ring-blue-500 dark:focus:ring-blue-500"
                            :disabled="!eventForm.customer"
                        >
                            <option value="" class="dark:bg-gray-700">{{ $t('calendar.select_project') }}</option>
                            <option
                                v-for="project in filteredProjects"
                                :key="project.project.id"
                                :value="project.project.id"
                                class="dark:bg-gray-700"
                            >
                                {{ project.project.name }}
                            </option>
                        </select>
                    </div>

                    <div>
                        <label class="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-900">{{ $t('calendar.activity') }}</label>
                        <select
                            v-model="eventForm.activity"
                            class="w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 focus:border-blue-500 dark:focus:border-blue-500 focus:ring-blue-500 dark:focus:ring-blue-500"
                        >
                            <option value="" class="dark:bg-gray-700">{{ $t('calendar.select_activity') }}</option>
                            <option
                                v-for="activity in activities"
                                :key="activity.activity.id"
                                :value="activity.activity.id"
                                class="dark:bg-gray-700"
                            >
                                {{ activity.activity.name }}
                            </option>
                        </select>
                    </div>

                    <div>
                        <label class="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-900">{{ $t('calendar.description') }}</label>
                        <textarea
                            v-model="eventForm.description"
                            class="w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 focus:border-blue-500 dark:focus:border-blue-500 focus:ring-blue-500 dark:focus:ring-blue-500"
                            rows="3"
                        ></textarea>
                    </div>
                </div>

                <div class="mt-6 flex justify-end space-x-3">
                    <button
                        @click="closeEventModal"
                        class="px-4 py-2 text-gray-700 dark:text-gray-600 hover:text-gray-900 dark:hover:text-gray-900 transition-colors"
                    >
                        {{ $t('calendar.cancel') }}
                    </button>
                    <button
                        v-if="selectedEvent"
                        @click="deleteEvent"
                        class="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-hidden focus:ring-2 focus:ring-red-500 focus:ring-offset-2 transition-colors"
                    >
                        {{ $t('calendar.delete') }}
                    </button>
                    <button
                        @click="saveEvent"
                        class="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-hidden focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
                    >
                        {{ $t('calendar.save') }}
                    </button>
                </div>
            </div>
        </div>
    </div>
    <ErrorModal
        :show="showErrorModal"
        :title="errorModalTitle"
        :message="errorModalMessage"
        @close="closeErrorModal"
    />
</template>

<script setup>
import {DayPilot, DayPilotCalendar, DayPilotNavigator} from '@daypilot/daypilot-lite-vue';
import { ref, reactive, onMounted, onBeforeUnmount, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import ErrorModal from './ErrorModal.vue';

const { t } = useI18n();

const props = defineProps({
    startdate: {
        type: String,
        required: true
    },
    title: {
        type: String,
        default: 'Time Entries'
    },
    days: {
        type: Number,
        default: 7
    }
});

// Ensure we start from Monday of the given week
const getWeekStart = (date) => {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1); // adjust when day is sunday
    return new Date(d.setDate(diff)).toISOString().split('T')[0];
};

const showEventModal = ref(false);
const selectedEvent = ref(null);
const customers = ref([]);
const projects = ref([]);
const activities = ref([]);

const eventForm = reactive({
    customer: '',
    project: '',
    activity: '',
    description: '',
    date: '',
    start: '',
    end: '',
    ticket: ''
});

const filteredProjects = computed(() => {
    if (!eventForm.customer) return [];
    return projects.value.filter(project =>
        project.project.customer === eventForm.customer
    );
});
// console.log(props.startdate);
const calendarConfig = reactive({
    locale: "de-de",
    viewType: "Week",
    startDate: props.startdate, // Use the calculated week start
    businessBeginsHour: 8,
    businessEndsHour: 23,
    heightSpec: "BusinessHoursNoScroll",
    durationBarVisible: false,
    timeRangeSelectedHandling: "JavaScript",
    showCurrentTimeMode: "Full",
    showCurrentTime: true,
    allowEventOverlap: true,
    weekStarts: 1, // 1 = Monday
    cellDuration: 15,
    timeHeaderCellDuration: 15,
    scale: "CellDuration",
    cellHeight: 15, // Adjust the height of each 15-minute cell
    snapToGrid: true,

    // Add this to highlight the current day
    headerDateFormat: "ddd dd.MM",
    onBeforeHeaderRender: (args) => {
        if (!args.header) return;

        const today = new Date();
        const headerDate = new Date(args.header.start);
        args.header.areas = args.header.areas || [];

        if (headerDate.toDateString() === today.toDateString()) {
            args.header.backColor = "#fbbf24"; // Amber-400 to match your theme
            args.header.fontColor = "#000000";
            args.header.areas.push({
                right: 2,
                top: 2,
                height: 16,
                width: 16,
                html: "•",
                fontColor: "#000000",
            });
        }
    },
    onTimeRangeSelected: (args) => {
        eventForm.date = args.start.value.split('T')[0];
        eventForm.start = args.start.value.split('T')[1];
        eventForm.end = args.end.value.split('T')[1];
        showEventModal.value = true;
    },
    onEventClick: (args) => {
        selectedEvent.value = args.e.data;
        // console.log('Selected event:', selectedEvent.value);
        Object.assign(eventForm, {
            customer: args.e.data.customer,
            project: args.e.data.project,
            activity: args.e.data.activity,
            description: args.e.data.description,
            date: args.e.data.start.split('T')[0],
            start: args.e.data.start.split('T')[1],
            end: args.e.data.end.split('T')[1],
            ticket: args.e.data.ticket || '' // Changed from jiraId to ticket
        });
        showEventModal.value = true;
    },

    onBeforeEventRender: (args) => {
        const now = new Date();
        const eventStart = new Date(args.data.start);

        if (eventStart > now) {
            args.data.backColor = '#ff4444';  // Red background for future events
            args.data.borderColor = '#cc0000'; // Darker red border
            args.data.fontColor = '#ffffff';   // White text for better contrast
        }
    },
    eventMoveHandling: "Update",
    eventResizeHandling: "Update",
    onEventMove: async (args) => {
        try {
            const formData = {
                id: args.e.data.id,
                date: args.newStart.value.split('T')[0],
                start: args.newStart.value.split('T')[1],
                end: args.newEnd.value.split('T')[1],
                customer: args.e.data.customer,
                project: args.e.data.project,
                activity: args.e.data.activity,
                description: args.e.data.description,
                ticket: args.e.data.ticket
            };

            const response = await fetch('/tools/time/tracking/save', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                },
                body: new URLSearchParams(formData)
            });

            if (!response.ok) {
                throw new Error('Failed to save event');
            }

            loadEvents(); // Reload events to ensure consistency
        } catch (error) {
            console.error('Error saving moved event:', error);
            args.preventDefault(); // Prevent the move if save failed
        }
    },
    onEventResize: async (args) => {
        try {
            const formData = {
                id: args.e.data.id,
                date: args.newStart.value.split('T')[0],
                start: args.newStart.value.split('T')[1],
                end: args.newEnd.value.split('T')[1],
                customer: args.e.data.customer,
                project: args.e.data.project,
                activity: args.e.data.activity,
                description: args.e.data.description,
                ticket: args.e.data.ticket
            };

            const response = await fetch('/tools/time/tracking/save', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                },
                body: new URLSearchParams(formData)
            });

            if (!response.ok) {
                throw new Error('Failed to save event');
            }

            loadEvents(); // Reload events to ensure consistency
        } catch (error) {
            console.error('Error saving resized event:', error);
            args.preventDefault(); // Prevent the resize if save failed
        }
    },
});

const calendarRef = ref(null);

const closeEventModal = () => {
    showEventModal.value = false;
    selectedEvent.value = null;
    Object.assign(eventForm, {
        customer: '',
        project: '',
        activity: '',
        description: '',
        date: '',
        start: '',
        end: '',
        ticket: '' // Changed from jiraId to ticket
    });
};

const saveEvent = async () => {
    try {
        const formData = {
            date: eventForm.date,
            start: eventForm.start,
            end: eventForm.end,
            customer: eventForm.customer,
            project: eventForm.project,
            activity: eventForm.activity,
            description: eventForm.description,
            ticket: eventForm.ticket // Changed from jiraId to ticket
        };

        if (selectedEvent.value?.id) {
            formData.id = selectedEvent.value.id;
        }

        const response = await fetch('/tools/time/tracking/save', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded',
            },
            body: new URLSearchParams(formData)
        });

        if (!response.ok) throw new Error('Failed to save event');

        closeEventModal();
        loadEvents();
    } catch (error) {
        console.error('Error saving event:', error);
        showError('Speichern fehlgeschlagen', 'Der Eintrag konnte nicht gespeichert werden. Bitte versuchen Sie es später erneut.');
    }
};

const deleteEvent = async () => {
    if (!selectedEvent.value?.id) return;

    try {
        const response = await fetch('/tools/time/tracking/delete', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded',
            },
            body: new URLSearchParams({
                id: selectedEvent.value.id
            })
        });

        if (!response.ok) throw new Error('Failed to delete event');

        closeEventModal();
        loadEvents();
    } catch (error) {
        console.error('Error deleting event:', error);
        showError('Löschen fehlgeschlagen', 'Der Eintrag konnte nicht gelöscht werden. Bitte versuchen Sie es später erneut.');
    }
};

const loadEvents = async () => {
    try {
        const response = await fetch(`/tools/time/getData/days/${props.days}?startDate=${props.startdate}`);
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

        const data = await response.json();
        // console.log('Data loaded:', data);
        calendarConfig.events = data.map(entry => ({
            id: entry.entry.id,
            start: `${(entry.entry.date).replaceAll('/', '-')}T${entry.entry.start}`,
            end: `${(entry.entry.date).replaceAll('/', '-')}T${entry.entry.end}`,
            text: formatEventText(entry.entry),
            customer: entry.entry.customer,
            project: entry.entry.project,
            activity: entry.entry.activity,
            description: entry.entry.description,
            ticket: entry.entry.ticket,
            backColor: '#60a5fa', // bg-blue-400
            fontColor: '#000000'
        }));
        // console.log('Events loaded:', calendarConfig.events);
    } catch (error) {
        console.error('Failed to load events:', error);
        showError('Laden fehlgeschlagen', 'Die Ereignisse konnten nicht geladen werden. Bitte versuchen Sie es später erneut.');
    }
};

const formatEventText = (entry) => {
    const customerName = customers.value.find(c => c.customer.id === entry.customer)?.customer.name || 'Unknown Customer';
    const projectName = projects.value.find(p => p.project.id === entry.project)?.project.name || 'Unknown Project';
    const ticket = entry.ticket ? `[${entry.ticket}] ` : '';

    return `${projectName} (${customerName})\n${ticket}${entry.description}`;
};

// Add a computed property for filtered customers
const activeCustomers = computed(() => {
    return customers.value.filter(customer =>
        customer.customer.active === true || customer.customer.active === 1
    );
});

// Load reference data
const loadCustomers = async () => {
    try {
        const response = await fetch('/tools/time/getAllCustomers');
        const data = await response.json();
        customers.value = data;
    } catch (error) {
        console.error('Failed to load customers:', error);
    }
};

const loadProjects = async () => {
    try {
        const response = await fetch('/tools/time/getAllProjects');
        const data = await response.json();
        projects.value = data;
    } catch (error) {
        console.error('Failed to load projects:', error);
    }
};

const loadActivities = async () => {
    // Implement activity loading
    activities.value = await fetch('/tools/time/getActivities').then(r => r.json());
};

// Add keyboard event handler
const handleKeydown = (event) => {
    if (event.key === 'Escape' && showEventModal.value) {
        closeEventModal();
    }
};

onMounted(async () => {
    await Promise.all([
        loadCustomers(),
        loadProjects()
    ]);
    loadEvents();
    loadActivities();
    document.addEventListener('keydown', handleKeydown);
});

onBeforeUnmount(() => {
    document.removeEventListener('keydown', handleKeydown);
});

// Add these new functions for week navigation
const getPreviousWeek = () => {
    const date = new Date(props.startdate);
    date.setUTCHours(12); // Set to noon UTC to avoid timezone issues
    date.setDate(date.getDate() - 7);
    return date.toISOString().slice(0, 10); // Use slice instead of split to get YYYY-MM-DD
};

const getNextWeek = () => {
    const date = new Date(props.startdate);
    date.setUTCHours(12); // Set to noon UTC to avoid timezone issues
    date.setDate(date.getDate() + 7);
    return date.toISOString().slice(0, 10); // Use slice instead of split to get YYYY-MM-DD
};

const handleCustomerChange = () => {
    // Reset project selection when customer changes
    eventForm.project = '';
};

// Add these new functions
const getCurrentWeekStart = () => {
    const today = new Date();
    const day = today.getDay();
    const diff = today.getDate() - day + (day === 0 ? -6 : 1);
    today.setDate(diff);
    today.setUTCHours(12);
    return today.toISOString().slice(0, 10);
};

const getCurrentWeekNumber = () => {
    const date = new Date(props.startdate);
    date.setUTCHours(12);
    // Get ISO week number
    date.setHours(0, 0, 0, 0);
    date.setDate(date.getDate() + 3 - (date.getDay() + 6) % 7);
    const week1 = new Date(date.getFullYear(), 0, 4);
    return 1 + Math.round(((date.getTime() - week1.getTime()) / 86400000 - 3 + (week1.getDay() + 6) % 7) / 7);
};

const formatDateRange = () => {
    const startDate = new Date(props.startdate);
    startDate.setUTCHours(12);
    const endDate = new Date(startDate);
    endDate.setDate(endDate.getDate() + 6);

    return `${startDate.getDate()}.${startDate.getMonth() + 1}. - ${endDate.getDate()}.${endDate.getMonth() + 1}.${endDate.getFullYear()}`;
};

// Add new function to handle Jira ID changes
const handleTicketChange = () => {
    if (!eventForm.ticket) return;

    // Convert to uppercase and trim
    eventForm.ticket = eventForm.ticket.toUpperCase().trim();

    // Extract project prefix (everything before the hyphen)
    const prefix = eventForm.ticket.split('-')[0];

    // Find matching project
    const matchingProject = projects.value.find(p =>
        p.project.jiraId === prefix ||
        (p.project.jiraTicket && p.project.jiraTicket.split(',').some(ticket => ticket.trim().startsWith(prefix)))
    );

    if (matchingProject) {
        // Set customer first
        eventForm.customer = matchingProject.project.customer;
        // Then set project
        eventForm.project = matchingProject.project.id;
    }
};

// Add these new refs for error handling
const showErrorModal = ref(false);
const errorModalTitle = ref('Fehler');
const errorModalMessage = ref('Bitte versuchen Sie es später erneut.');

// Add error handling helper function
const showError = (title = 'Fehler', message = 'Bitte versuchen Sie es später erneut.') => {
    errorModalTitle.value = title;
    errorModalMessage.value = message;
    showErrorModal.value = true;
};

const closeErrorModal = () => {
    showErrorModal.value = false;
};
</script>

<style>
.wrap {
    display: flex;
    flex-direction: column;
}

.content {
    flex-grow: 1;
}

.calendar_default_event_inner {
    border-radius: 5px;
    opacity: 0.9;
    padding: 4px !important;
    font-size: 12px !important;
    white-space: pre-wrap !important;
    line-height: 1.2 !important;
}

.calendar_default_event_inner.future {
    background-color: #ff4444 !important;
    border-color: #cc0000 !important;
    color: #ffffff !important;
}
</style>
